import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Box,
  Text,
  Group,
  Flex,
  Badge,
  Paper,
  Table,
  Skeleton,
  useMantineTheme,
} from '@mantine/core';
import { IconChartBar } from '@tabler/icons-react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';

export type AggregateVariationAdsParams = {
  visiopt_campaign_id: string[];
  filter_visiopt_campaign_ids: string[];
  goal_key: string;
  goal_label: string;
  variation_id: string;
  variation_name: string;
  attribution_model: string;
  // The report's attribution window, so the modal counts what the variation row counted.
  attribute_window_type?: string;
  lookback_days?: number;
  report_time: string;
};

type VariationAd = {
  traffic_source: string;
  campaign_name: string;
  adset: string;
  ad_name: string;
  conversions: number;
};

export function AggregateVariationAds({
  opened,
  onClose,
  params,
}: {
  opened: boolean;
  onClose: () => void;
  params: AggregateVariationAdsParams | null;
}) {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';
  const surfaceBg     = isDark ? theme.colors.dark[6] : theme.colors.gray[0];
  const rowBorder     = isDark ? theme.colors.dark[5] : theme.colors.gray[2];
  const headerLabelCo = isDark ? theme.colors.dark[1] : theme.colors.gray[6];
  const numericCo     = isDark ? theme.colors.gray[3] : theme.colors.gray[8];

  const [ads, setAds] = useState<VariationAd[]>([]);
  const [loading, setLoading] = useState(false);
  // Reopening fast must not let an earlier response overwrite the current one.
  const requestSeq = useRef(0);

  const openKey = opened && params ? `${params.goal_key}|${params.variation_id}` : '';

  useEffect(() => {
    if (openKey === '' || !params) { return; }
    setAds([]);
    setLoading(true);
    const seq = ++requestSeq.current;
    AggregatedReportsAPI
      .getvariationads({
        visiopt_campaign_id: params.visiopt_campaign_id,
        // Report-wide selection, so the drawer attributes the way its variation row did.
        filter_visiopt_campaign_ids: params.filter_visiopt_campaign_ids,
        goal_key: params.goal_key,
        variation_id: params.variation_id,
        attribution_model: params.attribution_model,
        attribute_window_type: params.attribute_window_type,
        lookback_days: params.lookback_days,
        report_time: params.report_time,
      })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (seq !== requestSeq.current) { return; }
        setAds(data && data.status === 1 && Array.isArray(data.data?.ads) ? data.data.ads : []);
      })
      .catch((e) => console.log(e))
      .finally(() => { if (seq === requestSeq.current) { setLoading(false); } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  const totalConversions = ads.reduce((sum, a) => sum + Number(a.conversions || 0), 0);
  const columns = ['Traffic Source', 'Campaign Name', 'Ad Set / Group Name', 'Ad Name', 'Conversions'];

  return (
    <Modal.Root opened={opened} onClose={onClose} size="80%" trapFocus={false}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header sx={{ paddingBottom: 12 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Group spacing={8} mb={2} noWrap>
              <Text size="lg" weight={700} sx={{ lineHeight: 1.2 }}>Variation Details</Text>
              {params && (
                <Badge size="md" variant="light" color="teal" sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {params.variation_name}
                </Badge>
              )}
            </Group>
            {params && (
              <Text size="sm" color="dimmed" sx={{ wordBreak: 'break-word' }}>
                {params.goal_label} · {totalConversions.toLocaleString()} conversion
                {totalConversions === 1 ? '' : 's'} across {ads.length} ad{ads.length === 1 ? '' : 's'}
              </Text>
            )}
          </Box>
          <Modal.CloseButton />
        </Modal.Header>

        <Modal.Body sx={{ paddingTop: 0 }}>
          <Paper
            withBorder
            radius="md"
            sx={{ backgroundColor: 'transparent', borderColor: rowBorder, overflow: 'hidden' }}
          >
            {loading ? (
              <Flex direction="column" gap="xs" p="sm">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} height={38} radius="sm" />
                ))}
              </Flex>
            ) : ads.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={48}>
                <IconChartBar size="2rem" stroke={1.4} color={headerLabelCo} />
                <Text size="sm" mt={8} color="dimmed">No ad data for this variation.</Text>
              </Flex>
            ) : (
              <SimpleBar autoHide={false} style={{ maxHeight: 460 }}>
                <Table
                  withColumnBorders
                  sx={{
                    marginBottom: 0,
                    'th, td': { borderColor: `${rowBorder} !important` },
                  }}
                >
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: surfaceBg, zIndex: 1 }}>
                    <tr>
                      {columns.map((c) => (
                        <th key={c} style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                          <Text size="xs" transform="uppercase" weight={700} sx={{ color: headerLabelCo, letterSpacing: 0.4 }}>
                            {c}
                          </Text>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ads.map((ad, i) => (
                      <tr key={`${ad.ad_name}-${i}`} style={{ borderTop: `1px solid ${rowBorder}`, verticalAlign: 'top' }}>
                        <td style={{ padding: '10px 12px' }}>
                          <Badge size="md" variant="light" color="blue" radius="sm" sx={{ textTransform: 'none', fontWeight: 600 }}>
                            {ad.traffic_source || '-'}
                          </Badge>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <Text size="sm" sx={{ overflowWrap: 'anywhere' }}>{ad.campaign_name || '-'}</Text>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <Text size="sm" sx={{ overflowWrap: 'anywhere' }}>{ad.adset || '-'}</Text>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <Text size="sm" sx={{ overflowWrap: 'anywhere' }}>{ad.ad_name || '-'}</Text>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <Text size="sm" weight={600} sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
                            {Number(ad.conversions || 0).toLocaleString()}
                          </Text>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </SimpleBar>
            )}
          </Paper>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
